import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Radio, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BLANK = { name: "", platform: "youtube", channel_url: "", game: "", notes: "" };

export default function CreatorRosterAdmin() {
  const [creators, setCreators] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/admin/creators`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setCreators(d.creators || []))
      .catch(() => toast.error("Could not load the creator roster."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/creators`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not add creator.");
      toast.success(`${form.name} added — Yorick will shout them out on the next rotation.`);
      setForm(BLANK); load();
    } catch (e2) { toast.error(e2.message); } finally { setBusy(false); }
  };

  const toggle = async (creator) => {
    const status = creator.status === "active" ? "disabled" : "active";
    const res = await fetch(`${API}/admin/creators/${encodeURIComponent(creator.id)}/status`, {
      method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
    });
    if (res.ok) { toast.success(`${creator.name} ${status === "active" ? "re-activated" : "paused"}.`); load(); }
    else toast.error("Could not update status.");
  };

  return (
    <section className="glass rounded-3xl p-6 md:p-7 mb-8 border border-gp-green/25" data-testid="admin-creators">
      <div>
        <h2 className="font-unbounded font-bold text-xl">Creator spotlights (Yorick AI)</h2>
        <p className="text-sm text-white/55 mt-1">Add streamers/creators once — Yorick writes a fresh shoutout and posts it to the news board on its own every 48h, linking to their own channel. No per-post review needed.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6 mt-5">
        <div className="space-y-2 max-h-[420px] overflow-y-auto gp-scroll pr-1">
          {creators.map((c) => <div key={c.id} className="rounded-xl border border-white/10 bg-black/20 p-3">
            <div className="flex justify-between gap-2"><span className="font-semibold text-sm">{c.name}</span><span className={`text-xs ${c.status === "active" ? "text-gp-green" : "text-white/40"}`}>{c.status === "active" ? "Active" : "Paused"}</span></div>
            <p className="text-xs text-white/55 mt-1">{c.platform} · {c.game || "any game"}</p>
            <p className="text-xs text-white/40 mt-1">{c.spotlight_count} shoutout(s) so far</p>
            <Button type="button" onClick={() => toggle(c)} variant="outline" size="sm" className="mt-2 rounded-full border-white/15">
              {c.status === "active" ? <ShieldOff size={13} className="mr-1.5" /> : <Radio size={13} className="mr-1.5" />}{c.status === "active" ? "Pause" : "Re-activate"}
            </Button>
          </div>)}
          {creators.length === 0 && <p className="text-sm text-white/40 p-3">No creators yet — add one to start the rotation.</p>}
        </div>

        <form onSubmit={add} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input required value={form.name} onChange={(e) => setForm((o) => ({ ...o, name: e.target.value }))} placeholder="Creator name" className="bg-black/25 border-white/10" />
            <select value={form.platform} onChange={(e) => setForm((o) => ({ ...o, platform: e.target.value }))} className="rounded-md border border-white/10 bg-gp-deep px-3 py-2 text-sm">
              <option value="youtube">YouTube</option>
              <option value="twitch">Twitch</option>
            </select>
          </div>
          <Input required type="url" value={form.channel_url} onChange={(e) => setForm((o) => ({ ...o, channel_url: e.target.value }))} placeholder="https://www.youtube.com/@handle or https://www.twitch.tv/handle" className="bg-black/25 border-white/10" />
          <Input value={form.game} onChange={(e) => setForm((o) => ({ ...o, game: e.target.value }))} placeholder="Game they're known for (optional)" className="bg-black/25 border-white/10" />
          <Textarea value={form.notes} onChange={(e) => setForm((o) => ({ ...o, notes: e.target.value }))} placeholder="What makes them worth watching? (helps Yorick write something specific, not generic)" className="bg-black/25 border-white/10" rows={2} />
          <Button type="submit" disabled={busy} className="rounded-full bg-gp-green text-black">
            {busy ? <Loader2 className="animate-spin mr-2" size={15} /> : <Plus className="mr-2" size={15} />}Add to roster
          </Button>
        </form>
      </div>
    </section>
  );
}
